import { useMemo } from 'react';

import { DEFAULT_TRT_APPT_CODE, DEFAULT_WH_APPT_CODE } from 'constants/defaults';
import { TRT_PROGRAM_APPT_CODE } from 'constants/trtAppointments';
import { WH_PROGRAM_APPT_CODE } from 'constants/whAppointments';
import { useGetMembershipPlansQuery } from 'services/lookup/lookup';

import { selectUser } from 'store';

import { useAppSelector } from 'hooks';
import { ProgramCategory, ProgramCode, ProgramStatus, WMDeviceFlow } from 'models/programs.types';
import { selectPrograms } from 'store/programs/programsSlice';
import { hasInsurancePricePoint, isInsurancePricePoint } from 'utils/insurance';
import { findProgramPricePoint } from 'utils/plans';

export const useMyPrograms = () => {
  const {
    data: membershipPlans = [],
    isFetching: isFetchingPlans,
    isLoading: isMembershipPlansLoading
  } = useGetMembershipPlansQuery();

  const user = useAppSelector(selectUser);
  const { programs = [] } = useAppSelector(selectPrograms);
  const {
    activePricePoints,
    activeProgramIDs,
    activePrograms,
    concierge,
    flexCare,
    freemium,
    hadOrHasMentalHealth,
    hadOrHasWeightManagement,
    hadOrHasWomensHealth,
    inactivePrograms,
    isInsurancePatient,
    isWmInsurancePatient,
    lifeMDPlus,
    mensHealth,
    mentalHealth,
    preferredMedications,
    totalCare,
    weightManagement,
    womensHealth
  } = useMemo(() => {
    const activePrograms = programs.filter((p) => p.program.status === ProgramStatus.Active);
    const inactivePrograms = programs.filter((p) => p.program.status === ProgramStatus.Inactive);
    const activeProgramsIds = activePrograms.map((p) => p.program._id);
    const preferredMedications = activePrograms.flatMap((p) =>
      p.details?.preferredMedication || p.details?.preferredMedication === null
        ? [{ preferredMedication: p.details.preferredMedication, program: p.program }]
        : []
    );

    const activePlans = membershipPlans.filter((p) =>
      p.pricePoints?.some((pp) => activeProgramsIds.includes(pp.programId))
    );
    const activePricePointsData = activePlans.flatMap((p) =>
      p.pricePoints?.filter((pp) => activeProgramsIds.includes(pp.programId))
    );

    const activePricePoints = activePrograms?.map((p) => p.program.planPricePointId) ?? [];

    const programPricePointsData = activePricePointsData.filter((pp) =>
      activePricePoints.includes(pp.planPricePointId)
    );

    const isInsurancePatient = hasInsurancePricePoint(programPricePointsData);

    const weightManagement = activePrograms.find((p) => p.program.category === 'weight-management');

    const wmPricePoint = findProgramPricePoint(activePricePointsData, weightManagement?.program);

    // boolean | undefined — `undefined` means "not yet known", never "not insured". A failed plans
    // lookup leaves `membershipPlans` at the `= []` default above, which keeps `plansReady` false.
    // `plansReady` is global, so it stays true when the fetched plans simply do not contain the WM
    // price point; `wmResolved` keeps that case unknown instead of reporting "not insured".
    const plansReady = !isFetchingPlans && membershipPlans.length > 0;
    const wmResolved = !weightManagement || !!wmPricePoint;
    const isWmInsurancePatient =
      plansReady && wmResolved ? isInsurancePricePoint(wmPricePoint) : undefined;

    return {
      activePricePoints,
      activeProgramIDs: activePrograms.map((p) => p.program._id),
      activePrograms,
      concierge: activePrograms.find((p) => p.program.code === ProgramCode.ConciergeCare),
      flexCare: activePrograms.find((p) => p.program.code === ProgramCode.FlexCare),
      freemium: activePrograms.length === 0,
      hadOrHasMentalHealth: programs.some((p) => p.program.category === 'mental-health'),
      hadOrHasWeightManagement: programs.some((p) => p.program.category === 'weight-management'),
      hadOrHasWomensHealth: programs.some((p) => p.program.category === 'women-health'),
      inactivePrograms,
      isInsurancePatient,
      isWmInsurancePatient,
      lifeMDPlus: activePrograms.find((p) => p.program.code === ProgramCode.LifeMDPlus),
      mensHealth: activePrograms.find((p) => p.program.category === 'mens-health'),
      mentalHealth: activePrograms.find((p) => p.program.category === 'mental-health'),
      preferredMedications,
      totalCare: activePrograms.find((p) => p.program.code === ProgramCode.TotalCare),
      weightManagement,
      womensHealth: activePrograms.find((p) => p.program.category === 'women-health')
    };
  }, [programs, membershipPlans, isFetchingPlans]);

  const isTTPatient =
    !!weightManagement && weightManagement.program.code === ProgramCode.WMTripleTherapy;

  const getProgramById = (programId: string) =>
    programs.find((program) => program.program._id === programId);

  const getProgramByCategory = (category: ProgramCategory) =>
    programs.find((program) => program.program.category === category);

  const wasWeightManagement = !weightManagement && hadOrHasWeightManagement;
  const wasMentalHealth = !mentalHealth && hadOrHasMentalHealth;
  const wasWomensHealth = !womensHealth && hadOrHasWomensHealth;

  const isHrtClub = womensHealth?.program.code === ProgramCode.WomenHealthHRTClub;

  const whAppointmentCode = womensHealth
    ? (WH_PROGRAM_APPT_CODE[womensHealth.program.code] ?? DEFAULT_WH_APPT_CODE)
    : DEFAULT_WH_APPT_CODE;

  const trtAppointmentCode = mensHealth
    ? (TRT_PROGRAM_APPT_CODE[mensHealth.program.code] ?? DEFAULT_TRT_APPT_CODE)
    : DEFAULT_TRT_APPT_CODE;

  const getPlanByCode = (planCode?: string) => {
    if (!planCode) return undefined;
    return membershipPlans.find((item) => item.planCode === planCode);
  };
  const getPlanById = (planId?: string) => {
    if (!planId) return undefined;
    return membershipPlans.find((item) => item._id === planId);
  };

  const getPricePointByPricePointId = (ppId?: string) => {
    if (!ppId) return null;
    for (const plan of membershipPlans) {
      const pricePoint = plan.pricePoints?.find((pp) => pp.planPricePointId === ppId);
      if (pricePoint) return pricePoint;
    }
    return null;
  };

  const getIsAsyncSignup = (category: ProgramCategory) => {
    // for now we only handle weight management and women health async signup flag
    if (!['weight-management', 'women-health'].includes(category)) return false;
    return user.isAsyncSignup;
    // will uncomment return getProgramByCategory(category)?.details?.isAsyncSignup;
  };

  /* eslint perfectionist/sort-objects: ["off"] */
  return {
    // mapped value
    activePricePoints,
    activeProgramIDs,

    // list of programs
    inactivePrograms,
    activePrograms,

    // individual ACTIVE programs - will be undefined if not active
    concierge,
    flexCare,
    mentalHealth,
    mensHealth,
    lifeMDPlus,
    totalCare,
    weightManagement,

    // helper to get program by id
    getProgramById,
    getPlanById,
    getPlanByCode,
    getProgramByCategory,
    getPricePointByPricePointId,
    getIsAsyncSignup,
    // boolean flags to determine patient's behavior
    hadOrHasWeightManagement,
    hadOrHasWomensHealth,
    isInsurancePatient,
    isMembershipPlansLoading,
    isWmInsurancePatient,
    isRWEPatient: user.wmDeviceFlow === WMDeviceFlow.WMDevices,
    isTTPatient,
    wasWeightManagement,
    wasMentalHealth,
    wasWomensHealth,
    womensHealth,
    isHrtClub,
    whAppointmentCode,
    trtAppointmentCode,
    freemium,
    preferredMedications
  };
};
